import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useUserStore } from '@/stores/UserStore'
import useConfigurationGlobals from "@/composables/useConfigurationGlobals";

const { $BRAND } = useConfigurationGlobals();

const apiKeys = {
  windalert: import.meta.env.VITE_GA4_ID_WINDALERT,
  ikitesurf: import.meta.env.VITE_GA4_ID_IKITESURF,
  iwindsurf: import.meta.env.VITE_GA4_ID_IWINDSURF,
  sailflow: import.meta.env.VITE_GA4_ID_SAILFLOW,
  fishweather: import.meta.env.VITE_GA4_ID_FISHWEATHER
}

export let useGoogleAnalyticsStore = defineStore('googleAnalyticsStore', () => {
  const userStore = useUserStore()

  const brand = $BRAND.toLowerCase()

  const loaded = ref(false)
  const scriptInserted = ref(false)
  const componentCallback = ref(null)

  const ga4Id = computed(() => apiKeys[brand])

  function loadScript(callback) {
    if (scriptInserted.value || loaded.value || window.dataLayer) {
      scriptLoadedCallback(callback)

      return
    }
    
    componentCallback.value = callback
    
    const googleAnalytcisScript = document.createElement('script')
    googleAnalytcisScript.onload = componentCallback.value
    googleAnalytcisScript.setAttribute(
      'src',
      `https://www.googletagmanager.com/gtag/js?id=${ga4Id.value}`
    )
    googleAnalytcisScript.async = true
    document.body.appendChild(googleAnalytcisScript)
    
    scriptInserted.value = true
  }

  function scriptLoadedCallback(callback) {
    if (!analyticsScriptLoaded() || !window.dataLayer) return;
    
    gtag('js', new Date())
    gtag('config', ga4Id.value, {
      Member_Level: userStore.memberLevelName
    })

    loaded.value = true

    if (callback && typeof callback === 'function') {
      callback()

      return
    }

    if (componentCallback.value && typeof componentCallback.value === 'function') {
      componentCallback.value()
    }
  }

  function analyticsScriptLoaded() {
    return (
      Object.keys(window.google_tag_manager || []).filter((e) => {
        return e.substring(0, 2) == 'G-'
      }).length > 0
    )
  }

  function sendEvent(name, params, debug = false) {
    if (!analyticsScriptLoaded()) {
      loadScript(() => scriptLoadedCallback(() => sendEvent(name, params, debug)))

      return
    }

    const defaultParameters = {}

    if (debug) {
      defaultParameters['debug_mode'] = true
    }

    const event = Object.assign(defaultParameters, params)

    window.dataLayer = window.dataLayer || []
    gtag('event', name, event)
  }

  function gtag() {
    dataLayer.push(arguments)
  }

  window.scriptLoadedCallback = scriptLoadedCallback

  return {
    loaded,
    loadScript,
    sendEvent
  }
})
